import React, { FunctionComponent, useCallback, useEffect, useState } from "react";
import { Select, Tooltip } from "antd";
import {
    MapBottomRightButtonWrapper,
    MapControlsExpandButton,
    MapControlsShrinkButton,
    MapControlsCard,
    MapCenterBottomRightButtonWrapper,
    MapCenterButton,
    MapDropdownFlexWrapper,
    MapDropdownWrapper,
} from "../Styled";
import { BodyText, getPolygonCentroid, Loading } from "../../../Common";
import { colorFour } from "../../../../globalStyles";
import {
    HomeWorkIdentifier,
    LayerTypeEnum,
    DropdownComparisonWeight,
    MapStyleEnum,
} from "../../../../Enums/Enums";
import { IGPSData } from "../../../../Models/IGPSData";
import { hexToRgb } from "../../../../Util/ColorUtil";
import { ChartTitle } from "../../../Common";
import { MapLayers } from "../../../../Util/MapLayers/MapLayers";
import { MapLayerOptions } from "../../../../Util/MapLayers/MapLayerOptions";
import { IPolygon } from "../../../../Models/Models";
import { MapTypesEnum } from "../../../../Enums/MapTypesEnum";
import { useSelector, useDispatch } from "react-redux";
import {
    createNewGoogleMap,
    setIsMapReady,
    setMapAnchorWindow,
    setMapStyleOptions,
} from "../../../../Actions/ReportMapActions";
import { ReportMap } from "../../../Common/Map/ReportMap";
import { IMobilyticsBenchmarkItem } from "../../../../Models/IMobilyticsBenchmarkItem";
import {
    filterStoreData,
    gpsReportMapSelector,
} from "../../../../Reducers/Selectors/GPSInsightsSelectors";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import {
    ReportMapLoading,
    ReportMapWrapper,
} from "../../../Common/Map/Styles/MapStyles";
import { RenderType } from "../../../../Enums/RenderType";
import {
    delay,
    digitalShareMapRender,
} from "../../../../Util/ScoutDigitalShareUtil";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import {
    digitalShareElementText,
    generationStatus,
    isOutputSelected,
    registerRef,
    setPendingRender,
} from "../../../Common/DigitalShare/DigitalShareGenerator";
import MobilyticsPolygonSelect from "../MobilyticsPolygonSelect";

const { Option } = Select;

const mapWrapperDomId = "gps-map-wrapper";

const CENTER_DELAY_1_MS_INITIAL = 1500;
const CENTER_DELAY_1_MS = 50;
const CENTER_DELAY_2_MS_INITIAL = 300;
const CENTER_DELAY_2_MS = 0;

const homeGradient = [
    [255, 140, 0],
    [255, 124, 0],
    [255, 105, 0],
    [255, 85, 0],
    [255, 58, 0],
    [255, 0, 0],
];
const workGradient = [
    [0, 140, 255],
    [145, 115, 246],
    [213, 76, 211],
    [255, 0, 156],
    [255, 0, 90],
    [255, 0, 0],
];

interface IGpsMapProps {
    storeData: IGPSData[];
    storePolygon: IPolygon[];
    benchmarkItems: IMobilyticsBenchmarkItem[];
    renderType: RenderType;
}

export const GPSMap: FunctionComponent<IGpsMapProps> = (props) => {
    const dispatch = useDispatch();
    const { storeData, storePolygon, renderType } = props;
    const {
        googleMap,
        mapZoom,
        currentAnchorWindow,
        buxtonDeck,
        mapStyleOptions,
    } = useSelector(gpsReportMapSelector);
    const [currentBenchmarkIndex, setCurrentBenchmarkIndex] = useState(0);

    /*State*/
    const [isVisits, setIsVisits] = useState(false);
    const [layerType, setLayerType] = useState(LayerTypeEnum.Scatterplot);
    const [isMapControlsOpen, setIsMapControlsOpen] = useState(false);
    const [mapNeedsRecenter, setMapNeedsRecenter] = useState(false);
    const [isHome, setIsHome] = useState(true);
    const [isMapLoaded, setMapLoaded] = useState<boolean>(false);

    /*Scatterplot Layer Filter*/
    const homeColorObj = hexToRgb("#00e5e6");
    const workColorObj = hexToRgb(colorFour);
    const [scatterplotOpacity, setScatterplotOpacity] = useState(0.5);
    const [scatterplotRadius, setScatterplotRadius] = useState(25);
    const [scatterplotLineWidth, setScatterplotLineWidth] = useState(1);
    const [scatterplotHomeColor, setScatterplotHomeColor] = useState([
        homeColorObj.r,
        homeColorObj.g,
        homeColorObj.b,
    ]);
    const [scatterplotWorkColor, setScatterplotWorkColor] = useState([
        workColorObj.r,
        workColorObj.g,
        workColorObj.b,
    ]);

    /*Heatmap Layer Filter*/
    const [heatmapOpacity, setHeatmapOpacity] = useState(0.5);
    const [heatmapRadius, setHeatmapRadius] = useState(50);
    const [heatmapIntensity, setHeatmapIntensity] = useState(2);
    const [heatmapThreshold, setHeatmapThreshold] = useState(0.05);

    /*Hexagon Layer Filter*/
    const [hexRadius, setHexRadius] = useState(500);
    const [hexOpacity, setHexOpacity] = useState(0.5);
    const [hexUpperPercentile, setHexUpperPercentile] = useState(100);
    const [hexLowerPercentile, setHexLowerPercentile] = useState(0);
    const [hexCoverage, setHexCoverage] = useState(0.85);

    /*Digital Share*/
    const [shareTemplate, setShareTemplate] = useState<React.ReactNode>(null);
    const [ref, setRef] = useState<React.MutableRefObject<HTMLDivElement>>(null);

    const gpsMapLayers = new MapLayers(
        buxtonDeck.deck,
        MapTypesEnum.GPSmap,
        isHome,
        isVisits
    );
    const infoWindowAnchor = new google.maps.MVCObject();

    useEffect(() => {
        let isStopped = false;
        let timeout = null;
        if (
            renderType === RenderType.DIGITAL_SHARE
            && isOutputSelected(
                DigitalShareAppType.Mobilytics,
                DigitalShareItemType.VisitorMap
            )
        ) {
            // Tell the system we're starting to render.
            setPendingRender(
                DigitalShareAppType.Mobilytics,
                DigitalShareItemType.VisitorMap,
                true
            );
            // Without this, we would see the Deck layer with opaque gray
            // background and no map.
            buxtonDeck.redraw();
            // Using a set timeout here to wait for the redraw() to take effect.
            timeout = setTimeout(() => {
                const run = async () => {
                    const ref = React.createRef<HTMLDivElement>();

                    // Note: It doesn't work to save the original values for these properties --
                    //     when we tried that, the entire image was scaled down past half size.
                    //     I'm guessing that querying these values from the DOM were causing a
                    //     browser reflow, and for some reason the height was recalculated wrong
                    //     at that time.
                    const originaliwBoxShadow = "0 2px 7px 1px rgb(0 0 0 / 30%)";
                    const originaliwBorderRadius = "8px";

                    const iw = document.querySelector(
                        `#${mapWrapperDomId} .gm-style-iw`
                    ) as HTMLElement;

                    if (iw) {
                        iw.style.backgroundColor = "white";
                        iw.style.boxShadow = "0 0 0 0 rgb(0 0 0 / 0%)";
                        iw.style.borderColor = "white";
                        iw.style.borderRadius = "0";
                        iw.style.color = "black";
                        iw.style.fontSize = "14px";
                        iw.style.paddingBottom = "10px";
                        iw.style.paddingRight = "10px";
                        const node = document.createTextNode(`${props.benchmarkItems[currentBenchmarkIndex].name}`);
                        iw.replaceChild(node, iw.children[0]);
                    }
                    const iwd = document.querySelector(
                        `#${mapWrapperDomId} .gm-style-iw-d`
                    ) as HTMLElement;
                    if (iwd) {
                        iwd.style.backgroundColor = "white";
                        iwd.style.borderColor = "white";
                    }

                    // Hide info window "close" button
                    const closeButton = document.querySelector(
                        `#${mapWrapperDomId} .gm-style-iw button`
                    ) as HTMLElement;
                    let originalCloseButtonDisplay = "";
                    if (closeButton) {
                        originalCloseButtonDisplay = closeButton.style.display;
                        closeButton.style.display = "none";
                    }

                    // Create an image from the map
                    const el = document.querySelector(
                        `#${mapWrapperDomId}`
                    ) as HTMLElement;
                    el.style.width = "1250px";
                    el.style.height = "700px";

                    const text = `Shows ${isHome ? "home" : "work"} locations of ${isVisits ? "visits" : "visitors"
                    } to ${props.benchmarkItems[currentBenchmarkIndex].name}.`;
                    digitalShareElementText.set(DigitalShareItemType.VisitorMap, text);

                    // Waiting here so that images can show up on the progress screen.
                    await delay(1000);

                    if (
                        // If the component has been cleaned up...
                        isStopped
                        // Or if there has been an error and the process has stopped...
                        || generationStatus.get(DigitalShareAppType.Mobilytics) === false
                    ) {
                        // Then tell the system we're not exporting this component anymore
                        // and don't do any more processing.
                        setPendingRender(
                            DigitalShareAppType.Mobilytics,
                            DigitalShareItemType.VisitorMap,
                            false
                        );
                    }
                    // Otherwise, if exporting should proceed...
                    else {
                        // Get the template element to plunk in the DOM so it can be accessed by the generator.
                        const template = await digitalShareMapRender(
                            DigitalShareAppType.Mobilytics,
                            DigitalShareItemType.VisitorMap,
                            el,
                            ref
                        );

                        setShareTemplate(template);
                        setRef(ref);
                        // Tell the system we're not rendering anymore (for cancellation cleanup).
                        setPendingRender(
                            DigitalShareAppType.Mobilytics,
                            DigitalShareItemType.VisitorMap,
                            false
                        );
                    }

                    // Restore info window styles
                    if (iw) {
                        iw.style.boxShadow = originaliwBoxShadow;
                        iw.style.borderRadius = originaliwBorderRadius;
                    }
                    // Restore info window "close" button
                    if (closeButton) {
                        closeButton.style.display = originalCloseButtonDisplay;
                    }
                    // Restore map dimensions
                    el.style.width = "";
                    el.style.height = "";
                };
                run();
            }, 100);
        }
        return () => {
            if (props.renderType === RenderType.DEFAULT) {
                isStopped = true;
            }

            clearTimeout(timeout);
        };
    }, [renderType, buxtonDeck]);

    useEffect(() => {
        if (ref && shareTemplate) {
            registerRef(
                DigitalShareAppType.Mobilytics,
                DigitalShareItemType.VisitorMap,
                ref
            );
        }
    }, [ref, shareTemplate]);

    // This useEffect is used to clean up the WebGL context when the component is unmounted.
    // It is necessary to prevent memory leaks.
    useEffect(() => {
        return () => {
            buxtonDeck.destroyWebGlContext(); // Cleanup
        };
    }, []);

    const drawLayers = (layerType, googleMap, mapData, isHome) => {
        let layer;
        const data = filterStoreData(mapData, isHome);
        switch (layerType) {
            case LayerTypeEnum.Scatterplot:
                layer = gpsMapLayers.createScatterplotLayer(
                    scatterplotHomeColor,
                    scatterplotWorkColor,
                    scatterplotRadius,
                    scatterplotLineWidth,
                    scatterplotOpacity,
                    data,
                    isHome
                );
                if (featureFlags.DigitalShare) {
                    (window as any)._scatterplotLayer = layer;
                }
                break;
            case LayerTypeEnum.Heatmap:
                layer = gpsMapLayers.createHeatmapLayer(
                    homeGradient,
                    workGradient,
                    heatmapOpacity,
                    heatmapRadius,
                    heatmapIntensity,
                    heatmapThreshold,
                    data,
                    isHome
                );
                break;
            case LayerTypeEnum.Hexagon:
                layer = gpsMapLayers.createHexagonLayer(
                    homeGradient,
                    workGradient,
                    hexOpacity,
                    hexRadius,
                    hexUpperPercentile,
                    hexLowerPercentile,
                    hexCoverage,
                    data,
                    isHome
                );
                break;
        }

        if (layer) {
            const polygonLayer = gpsMapLayers.createGpsPolygonLayer(
                props.storePolygon[currentBenchmarkIndex].linearRings,
                "deck-gps-polygon-layer",
                (x, y) => {
                    setInfoWindowAndOpen(x, y, currentAnchorWindow);
                },
                currentBenchmarkIndex
            );

            buxtonDeck.clearProps();
            buxtonDeck.addProp(layer, googleMap);
            buxtonDeck.addProp(polygonLayer, googleMap);
        }
    };

    useEffect(() => {
        drawLayers(
            layerType,
            googleMap,
            storeData[currentBenchmarkIndex].mapData,
            isHome
        );
    }, [layerType, isHome, isVisits]);

    useEffect(() => {
        if (mapNeedsRecenter) {
            let delay1Ms;
            let delay2Ms;
            if (isMapLoaded) {
                delay1Ms = CENTER_DELAY_1_MS;
                delay2Ms = CENTER_DELAY_2_MS;
                if (featureFlags.AllowOverrideTradeAreaMapDelay) {
                    if ((window as any).centerDelay1 !== undefined) {
                        delay1Ms = (window as any).centerDelay1;
                    }
                    if ((window as any).centerDelay2 !== undefined) {
                        delay2Ms = (window as any).centerDelay2;
                    }
                }
            } else {
                delay1Ms = CENTER_DELAY_1_MS_INITIAL;
                delay2Ms = CENTER_DELAY_2_MS_INITIAL;
                if (featureFlags.AllowOverrideTradeAreaMapDelay) {
                    if ((window as any).centerDelay1Initial !== undefined) {
                        delay1Ms = (window as any).centerDelay1Initial;
                    }
                    if ((window as any).centerDelay2Initial !== undefined) {
                        delay2Ms = (window as any).centerDelay2Initial;
                    }
                }
            }

            const centroid = getPolygonCentroid(
                storePolygon[currentBenchmarkIndex].linearRings[0]
            );
            googleMap.panTo({ lat: centroid.latitude, lng: centroid.longitude });
            googleMap.setZoom(mapZoom);

            let centerTimer = null;

            const deckTimer = setTimeout(() => {
                drawLayers(
                    layerType,
                    googleMap,
                    storeData[currentBenchmarkIndex].mapData,
                    isHome
                );

                centerTimer = setTimeout(() => {
                    addInfoWindow(centroid.latitude, centroid.longitude);
                    setMapNeedsRecenter(false);
                    setMapLoaded(true);
                    dispatch(setIsMapReady(true));
                }, delay2Ms);
            }, delay1Ms);

            return () => {
                clearTimeout(deckTimer);
                clearTimeout(centerTimer);
            };
        }
    }, [currentBenchmarkIndex, mapNeedsRecenter]);

    const addInfoWindow = (lat: number, lng: number) => {
        let infoWindow = currentAnchorWindow;
        if (infoWindow === null) {
            infoWindow = new google.maps.InfoWindow({
                pixelOffset: new google.maps.Size(0, -10),
                disableAutoPan: true,
            });
        }

        setInfoWindowAndOpen(lat, lng, infoWindow);

        if (currentAnchorWindow === null) {
            dispatch(setMapAnchorWindow(infoWindow));
        }
    };

    const refreshInfoWindow = (lat: number, lng: number) => {
        const infoWindow = new google.maps.InfoWindow({
            pixelOffset: new google.maps.Size(0, -10),
            disableAutoPan: true,

        });

        setInfoWindowAndOpen(lat, lng, infoWindow);

        if (currentAnchorWindow == null) {
            dispatch(setMapAnchorWindow(infoWindow));
        }
    };

    useEffect(() => {
        const data = filterStoreData(
            storeData[currentBenchmarkIndex].mapData,
            isHome
        );
        const layer = gpsMapLayers.createScatterplotLayer(
            scatterplotHomeColor,
            scatterplotWorkColor,
            scatterplotRadius,
            scatterplotLineWidth,
            scatterplotOpacity,
            data,
            isHome
        );

        buxtonDeck.updateProp(layer);
    }, [
        scatterplotOpacity,
        scatterplotRadius,
        scatterplotLineWidth,
        scatterplotHomeColor,
        scatterplotWorkColor,
    ]);

    useEffect(() => {
        const data = filterStoreData(
            storeData[currentBenchmarkIndex].mapData,
            isHome
        );
        const layer = gpsMapLayers.createHeatmapLayer(
            homeGradient,
            workGradient,
            heatmapOpacity,
            heatmapRadius,
            heatmapIntensity,
            heatmapThreshold,
            data,
            isHome
        );

        buxtonDeck.updateProp(layer);
    }, [heatmapOpacity, heatmapRadius, heatmapIntensity, heatmapThreshold]);

    useEffect(() => {
        const data = filterStoreData(
            storeData[currentBenchmarkIndex].mapData,
            isHome
        );
        const layer = gpsMapLayers.createHexagonLayer(
            homeGradient,
            workGradient,
            hexOpacity,
            hexRadius,
            hexUpperPercentile,
            hexLowerPercentile,
            hexCoverage,
            data,
            isHome
        );

        buxtonDeck.updateProp(layer);
    }, [
        hexOpacity,
        hexRadius,
        hexUpperPercentile,
        hexLowerPercentile,
        hexCoverage,
    ]);

    useEffect(() => {
        const centroid = getPolygonCentroid(
            storePolygon[currentBenchmarkIndex].linearRings[0]
        );
        addInfoWindow(centroid.latitude, centroid.longitude);
    }, [props.benchmarkItems]);

    const setInfoWindowAndOpen = (
        latitude: number,
        longitude: number,
        infoWindow: google.maps.InfoWindow
    ) => {
        infoWindowAnchor.set(
            "position",
            new google.maps.LatLng(latitude, longitude)
        );

        if (props.benchmarkItems[currentBenchmarkIndex].name) {
            infoWindow.setContent(props.benchmarkItems[currentBenchmarkIndex].name);
        } else {
            infoWindow.setContent(`Polygon around: ${latitude}, ${longitude}`);
        }

        if (googleMap !== null) {
            infoWindow.open(googleMap, infoWindowAnchor);
        }
    };

    const handleVisitsChange = (value: DropdownComparisonWeight) => {
        switch (value) {
            case DropdownComparisonWeight.PRIMARY:
                setIsVisits(false);
                break;
            case DropdownComparisonWeight.SECONDARY:
                setIsVisits(true);
                break;
        }
    };

    const handleHomeWorkChange = (value: HomeWorkIdentifier) => {
        setIsHome(value === HomeWorkIdentifier.HOME);
    };

    const handleLayerTypeChange = (value: LayerTypeEnum) => {
        switch (value) {
            case LayerTypeEnum.Hexagon:
                setLayerType(LayerTypeEnum.Hexagon);
                break;
            case LayerTypeEnum.Scatterplot:
                setLayerType(LayerTypeEnum.Scatterplot);
                setIsVisits(true);
                break;
            case LayerTypeEnum.Heatmap:
                setLayerType(LayerTypeEnum.Heatmap);
                break;
        }
    };

    const handleMapStyleChange = (style: MapStyleEnum) =>
        dispatch(setMapStyleOptions(style));

    const handleMapCenterReset = (): void => {
        const centroid = getPolygonCentroid(
            storePolygon[currentBenchmarkIndex].linearRings[0]
        );
        googleMap.setZoom(mapZoom);
        googleMap.panTo({ lat: centroid.latitude, lng: centroid.longitude });
    };

    const onShrink = () => {
        setIsMapControlsOpen(false);
    };

    const onExpand = () => {
        setIsMapControlsOpen(true);
    };

    const handleBenchmarkChange = (value: string) => {
        const index = Number(value);
        setCurrentBenchmarkIndex(index);
        setMapNeedsRecenter(true);
    };

    const getBenchmarkButtonClassName = (
        isSelected: boolean,
        index: number,
        arrayLength: number
    ) => {
        let className = "";
        if (index === 0) {
            className += "gps-benchmark-button-leftmost";
        } else if (index === arrayLength - 1) {
            className += "gps-benchmark-button-rightmost";
        } else {
            className += "gps-benchmark-button-middlemost";
        }

        if (isSelected) {
            className += " gps-benchmark-button-selected";
        }
        return className;
    };

    const onMapReady = useCallback(() => {
        setMapNeedsRecenter(true);
    }, []);

    return (
        <>
            <ChartTitle
                titleText={`${isVisits ? `Visits` : `Visitors`} Map`}
                tooltipText="Provides insights into the Home and Work locations by density for the geography of interest. Use the toggle switches found around the border to toggle different modes, themes, and ranges."
            />
            <BodyText>
                Provides insights into the Home and Work locations by density for the
                geography of interest. Use the toggle switches found around the border
                to toggle different modes, themes, and ranges.
            </BodyText>
            <MobilyticsPolygonSelect
                benchmarkItems={props.benchmarkItems}
                selectedIndex={currentBenchmarkIndex}
                onChange={handleBenchmarkChange}
            />
            {featureFlags.DigitalShare
                && renderType === RenderType.DIGITAL_SHARE
                // Template gets attached to DOM here so it can then be accessed
                // by the TemplateImageGenerator.
                && shareTemplate}

            {/* This is the element being printed */}
            <ReportMapWrapper id={mapWrapperDomId}>
                <div
                    id="deck-tooltip"
                    style={{
                        position: "absolute",
                        zIndex: 1,
                        pointerEvents: "none",
                        background: "none",
                    }}
                ></div>
                <ReportMap
                    map={googleMap}
                    mapDomId={"gps-map"}
                    onMapReady={onMapReady}
                    createMapAction={createNewGoogleMap}
                />
                {!isMapLoaded && (
                    <ReportMapLoading id="map-loading-div">
                        <Loading title="Map Loading" />
                    </ReportMapLoading>
                )}

                {renderType !== RenderType.DIGITAL_SHARE && (
                    // We don't want the buttons to show in the share/print versions.
                    <>
                        <MapDropdownFlexWrapper>
                            <MapDropdownWrapper>
                                <Tooltip placement="topLeft" title={"Location Identifier"}>
                                    <Select
                                        defaultValue={
                                            isHome ? HomeWorkIdentifier.HOME : HomeWorkIdentifier.WORK
                                        }
                                        onChange={handleHomeWorkChange}
                                    >
                                        <Option value={HomeWorkIdentifier.HOME}>Home</Option>
                                        <Option value={HomeWorkIdentifier.WORK}>Work</Option>
                                    </Select>
                                </Tooltip>
                            </MapDropdownWrapper>
                            <MapDropdownWrapper>
                                <Tooltip placement="top" title={"Map Style"}>
                                    <Select
                                        defaultValue={mapStyleOptions}
                                        onChange={handleMapStyleChange}
                                    >
                                        <Option value={MapStyleEnum.Light}>Light</Option>
                                        <Option value={MapStyleEnum.Dark}>Dark</Option>
                                    </Select>
                                </Tooltip>
                            </MapDropdownWrapper>
                            <MapDropdownWrapper>
                                <Tooltip placement="top" title={"Visualization Type"}>
                                    <Select
                                        defaultValue={layerType}
                                        onChange={handleLayerTypeChange}
                                    >
                                        <Option value={LayerTypeEnum.Scatterplot}>
                                            Households
                                        </Option>
                                        <Option value={LayerTypeEnum.Hexagon}>Hexagon</Option>
                                        <Option value={LayerTypeEnum.Heatmap}>Heatmap</Option>
                                    </Select>
                                </Tooltip>
                            </MapDropdownWrapper>
                            <MapDropdownWrapper>
                                {layerType !== LayerTypeEnum.Scatterplot && (
                                    <Tooltip placement="top" title={"Visits Type"}>
                                        <Select
                                            defaultValue={
                                                isVisits
                                                    ? DropdownComparisonWeight.SECONDARY
                                                    : DropdownComparisonWeight.PRIMARY
                                            }
                                            onChange={handleVisitsChange}
                                            style={{ marginRight: "16px" }}
                                        >
                                            <Option value={DropdownComparisonWeight.PRIMARY}>
                                                Visitors
                                            </Option>
                                            <Option value={DropdownComparisonWeight.SECONDARY}>
                                                Visits
                                            </Option>
                                        </Select>
                                    </Tooltip>
                                )}
                            </MapDropdownWrapper>
                        </MapDropdownFlexWrapper>
                        <MapCenterBottomRightButtonWrapper>
                            <Tooltip placement="left" title={"Center Map"}>
                                <MapCenterButton onClick={handleMapCenterReset} />
                            </Tooltip>
                        </MapCenterBottomRightButtonWrapper>
                        <MapBottomRightButtonWrapper>
                            {isMapControlsOpen ? (
                                <MapControlsCard
                                    title={layerType + " Options"}
                                    extra={<MapControlsShrinkButton onClick={onShrink} />}
                                >
                                    <MapLayerOptions
                                        isHome={isHome}
                                        layerType={layerType}
                                        scatterplotOpacity={scatterplotOpacity}
                                        scatterplotRadius={scatterplotRadius}
                                        scatterplotLineWidth={scatterplotLineWidth}
                                        scatterplotHomeColor={scatterplotHomeColor}
                                        scatterplotWorkColor={scatterplotWorkColor}
                                        setScatterplotOpacity={setScatterplotOpacity}
                                        setScatterplotRadius={setScatterplotRadius}
                                        setScatterplotLineWidth={setScatterplotLineWidth}
                                        setScatterplotHomeColor={setScatterplotHomeColor}
                                        setScatterplotWorkColor={setScatterplotWorkColor}
                                        heatmapOpacity={heatmapOpacity}
                                        heatmapRadius={heatmapRadius}
                                        heatmapIntensity={heatmapIntensity}
                                        heatmapThreshold={heatmapThreshold}
                                        setHeatmapRadius={setHeatmapRadius}
                                        setHeatmapOpacity={setHeatmapOpacity}
                                        setHeatmapThreshold={setHeatmapThreshold}
                                        setHeatmapIntensity={setHeatmapIntensity}
                                        hexOpacity={hexOpacity}
                                        hexRadius={hexRadius}
                                        hexLowerPercentile={hexLowerPercentile}
                                        hexUpperPercentile={hexUpperPercentile}
                                        hexCoverage={hexCoverage}
                                        setHexOpacity={setHexOpacity}
                                        setHexRadius={setHexRadius}
                                        setHexLowerPercentile={setHexLowerPercentile}
                                        setHexCoverage={setHexCoverage}
                                        setHexUpperPercentile={setHexUpperPercentile}
                                    />
                                </MapControlsCard>
                            ) : (
                                <Tooltip placement="left" title={"Visualization Controls"}>
                                    <MapControlsExpandButton onClick={onExpand} />
                                </Tooltip>
                            )}
                        </MapBottomRightButtonWrapper>
                    </>
                )}
            </ReportMapWrapper>
        </>
    );
};
